import { useSelector } from "react-redux";
import "./AmSchedule.scss";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    TablePagination,
} from "@mui/material";
import { motion } from "framer-motion";
import { GridLoader, ScaleLoader } from "react-spinners";
import newRequest from "../../../utils/newRequest";
import moment from "moment";
import numeral from "numeral";
import { useTranslation } from "react-i18next";
import { formatStringToNumberV2 } from "../../../utils/genericFunctions";

const AmSchedule = ({ tradeDocid, hdCurCode }) => {
    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [couponSchedule, setCouponSchedule] = useState([]);
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [totalCouponFcAmount, setTotalCouponFcAmount] = useState(0);
    const [totalCouponLcAmount, setTotalCouponLcAmount] = useState(0);
    const [totalPaidFcAmount, setTotalPaidFcAmount] = useState(0);
    const [totalPaidLcAmount, setTotalPaidLcAmount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);

    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const slicedCoupons = couponSchedule.slice(
        currentPage * rowsPerPage,
        currentPage * rowsPerPage + rowsPerPage
    );

    const handleRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 5));
        setCurrentPage(0);
    };


    const fetchCouponSchedule = async () => {
        try {
            setIsLoading(true);
            // console.log(`document id is ... ${tradeDocid}`);
            const res = await newRequest.get(`/trade/getSchedule/${tradeDocid}`);
            // console.log(`fetchCouponSchedule ${JSON.stringify(res.data, null, 2)}`);
            setCouponSchedule(res.data);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching entity types:", error);
        }
    };

    useEffect(() => {
        if (!Array.isArray(couponSchedule)) return;

        let fcCoupons = 0;
        let lcCoupons = 0;
        let fcPaid = 0;
        let lcPaid = 0;
        for (const item of couponSchedule) {
            const fc_coupon = parseFloat(formatStringToNumberV2(item?.coupon_amount_fc || 0)) || 0;
            const lc_coupon = parseFloat(formatStringToNumberV2(item?.coupon_amount_lc || 0)) || 0;
            fcCoupons += isNaN(fc_coupon) ? 0 : fc_coupon;
            lcCoupons += isNaN(lc_coupon) ? 0 : lc_coupon;

            const fc_paid = parseFloat(formatStringToNumberV2(item?.coupon_paid_fc || 0)) || 0;
            const lc_paid = parseFloat(formatStringToNumberV2(item?.coupon_paid_lc || 0)) || 0;
            fcPaid += isNaN(fc_paid) ? 0 : fc_paid;
            lcPaid += isNaN(lc_paid) ? 0 : lc_paid;
        }

        setTotalCouponFcAmount(fcCoupons);
        setTotalCouponLcAmount(lcCoupons);
        setTotalPaidFcAmount(fcPaid);
        setTotalPaidLcAmount(lcPaid);
    }, [couponSchedule]);

    useEffect(() => {
        fetchCouponSchedule();
    }, [tradeDocid]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
            className="am-schedule">
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
            color="var(--primary)"
            height={50}
            radius={8}
            width={4}
          />
                </div>
            ) : (
                <div className="am-schedule-wrapper">
                    {/* Policy Coupons */}
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "10px",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "10px"
                        }}
                    >
                        <span className="sub-title">{`${couponSchedule?.length} ${t('installments').toUpperCase()}`}</span>
                        <span style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "5px",
                            justifyContent: "center",
                            alignItems: "center"
                        }}>
                            <span className={`status Critical`} style={{ marginLeft: "10px", height: "32px", alignContent: "center", alignItems: "center" }}>{`${t('expected').toUpperCase()} ${numeral(totalCouponFcAmount).format("0,0.00")}`}</span>
                            <span className={`status Active`} style={{ marginLeft: "10px", height: "32px", alignContent: "center", alignItems: "center" }}>{`${t('paid').toUpperCase()} ${numeral(totalPaidFcAmount).format("0,0.00")}`}</span>
                            <span className={`status Validated`} style={{ marginLeft: "10px", height: "32px", alignContent: "center", alignItems: "center" }}>{`${t('os').toUpperCase()} ${numeral(totalPaidFcAmount - totalCouponFcAmount).format("0,0.00")}`}</span>
                        </span>
                    </div>
                    {couponSchedule?.length > 0 &&
                        <div className="table-content">
                            <TableContainer component={Paper} sx={{ border: 'none', boxShadow: 'none' }} className="table-container">
                                <Table sx={{ width: "max-content", border: 'none' }}>
                                    <TableHead className="tablehead">
                                        <TableRow className="tablerow">
                                            <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                #
                                            </TableCell>
                                            <TableCell className="table-cell" style={{ width: "auto" }}>
                                                <span>{t('installment').toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell right" style={{ maxWidth: "150px" }}>
                                                {`${t('amount').toUpperCase()} [${hdCurCode}]`}
                                            </TableCell>
                                            <TableCell className="table-cell right" style={{ maxWidth: "150px" }}>
                                                {t('received').toUpperCase()}
                                            </TableCell>
                                            <TableCell className="table-cell right" style={{ maxWidth: "150px" }}>
                                                {t('outstanding').toUpperCase()}
                                            </TableCell>
                                            <TableCell className="table-cell" style={{ maxWidth: "200px" }}>
                                                {t('receipts').toUpperCase()}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody className="tablebody">
                                        {slicedCoupons?.map((row, index) => (
                                            <TableRow
                                                key={row._id}
                                                className={`${selectedCoupon?._id === row._id ? "tablerow current" : "tablerow"}`}
                                                onClick={() => {
                                                    try {
                                                        setSelectedCoupon(row);
                                                    } catch (error) {
                                                        console.log(error);
                                                    }
                                                }}
                                            >
                                                <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                    <span>{row?.coupon_number}</span>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                    <span>{moment(row?.coupon_date).format("DD-MMM-YYYY")}</span>
                                                </TableCell>
                                                <TableCell className="table-cell right" style={{ maxWidth: "150px" }}>
                                                    <span>{numeral(row?.coupon_amount_fc).format("0,0.00")}</span>
                                                </TableCell>
                                                <TableCell className="table-cell right" style={{ maxWidth: "150px" }}>
                                                    <span>{numeral(row?.coupon_paid_fc).format("0,0.00")}</span>
                                                </TableCell>
                                                <TableCell className="table-cell right" style={{ maxWidth: "150px" }}>
                                                    <span>{numeral(row?.coupon_amount_fc - row?.coupon_paid_fc).format("0,0.00")}</span>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ maxWidth: "200px" }}>
                                                    <span />
                                                    {/* this span is a placeholder */}
                                                    {/* {row.receipts_xx?.length > 0 &&
                                                        <div className="chip-wrapper">
                                                            {row.receipts_xx.map((row) => (
                                                                <Chip
                                                                    key={row.ln_hd_no_docid}
                                                                    label={row.ln_hd_no}
                                                                    className="chip"
                                                                    onClick={() => {
                                                                        try {
                                                                            navigate(`/receipt/${row.ln_hd_no_docid}`, {
                                                                                state: { shouldFetchClients: false },
                                                                            });
                                                                        } catch (error) {
                                                                            console.log(error)
                                                                        }
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>} */}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {couponSchedule?.length > rowsPerPage &&
                                    <TablePagination
                                        rowsPerPageOptions={[10]}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={handleRowsPerPage}
                                        component="div"
                                        count={couponSchedule?.length}
                                        rows={couponSchedule}
                                        page={currentPage}
                                        onPageChange={handlePageChange}
                                    />}
                            </TableContainer>
                        </div>}
                    {/* End of installments table */}
                </div>
            )}
            {isProcessing && (
                <motion.div
                    className="processing-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="processing-wrapper">
                        <GridLoader
                            color="#F44336"
                            style={{
                                height: "50px",
                                width: "50px",
                            }}
                        />
                    </div>
                </motion.div>
            )}
        </motion.div>
    )
}

export default AmSchedule